import React, {
  Suspense,
  lazy,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import CryptoJS from "crypto-js";
import NewRouteGuard from "./Components/RouteGuard";
import RenovatedSidebar from "./Components/Sidebar";
import NewBarcode from "./Pages/OnBoard";
import "./Styles/app.scss";
import BlurLoading from "./Components/BlurLoading";
import useAuthorizationQrFlow from "./hooks/useAuthorizationQrFlow";
import useCardDetailsRevealFlow from "./hooks/useCardDetailsRevealFlow";
import useGlobalLoading from "./hooks/useGlobalLoading";
import useSidebarSession from "./Components/Sidebar/hooks/useSidebarSession";
import Navbar from "./Components/Navbar";
import useNewLogoutAPI from "./api/logoutAPI";
import { fetchTransactionsToApproveCount } from "../features/responseSlice";
import {
  addWebSocketEvent,
  setFeatureData,
  setWebSocketConnected,
} from "./store/slices/appSlice";
import InactivityTimeout from "../components/inactivity-logout";
import { inactivityDuration } from "../utils/Constants";
import OverviewPage from "./Pages/Overview";
import BillsPage from "./Pages/Bills";
import BankAccountOverviewPage from "./Pages/BankAccountOverview";
import StatementOfAccountPage from "./Pages/StatementOfAccount";
import MerchantInformationPage from "./Pages/MerchantInformation";
import { SESSION_STORAGE_KEYS } from "./constants/sessionStorage";
import { canUseHttpRecorder } from "./utils/httpRecorderConfig";
import Enrollment from "../pages/Enrollment";
import MakePaymentPage from "./Pages/MakePayment";
import BulkTransferComposer from "./Pages/BulkTransfer";
import Merchants from "./Pages/Merchants";

const SuyoolToSuyool = lazy(() => import("./Pages/SuyoolToSuyool"));
const BillServicePage = lazy(() =>
  import("./Pages/Bills/components/BillServicePage")
);
const ValidationPopup = lazy(() => import("./Components/ValidationPopup"));
const PortalActionsQr = lazy(() => import("./Components/PortalActionsQr"));
const AccountPage = lazy(() => import("./Pages/Account"));
const PayrollPage = lazy(() => import("./Pages/Payroll"));
const BslToSuyoolPage = lazy(() => import("./Pages/BslToSuyool"));
const BankAccountTransfersPage = lazy(() =>
  import("./Pages/BankAccountTransfers")
);
const BankAccountTransactionHistoryPage = lazy(() =>
  import("./Pages/BankAccountTransactionHistory")
);
const ExchangePage = lazy(() => import("./Pages/Exchange"));
const CashDeliveryPage = lazy(() => import("./Pages/CashDelivery"));
const EmployeesPage = lazy(() => import("./Pages/Employees"));
const PendingApprovalsPage = lazy(() => import("./Pages/PendingApprovals"));
const PendingSummaryPage = lazy(() =>
  import("./Pages/PendingApprovals/PendingSummaryPage")
);
const CardTransactionsPage = lazy(() => import("./Pages/CardTransactions"));
const CardsSettingsPage = lazy(() => import("./Pages/CardsSettings"));
const PayCorporationsPage = lazy(() => import("./Pages/PayCorporations"));
const MerchantEnrollmentPage = lazy(() => import("./Pages/MerchantEnrollment"));
const EodClosingPage = lazy(() => import("./Pages/EodClosing"));
const ExternalCashDepositPage = lazy(() =>
  import("./Pages/ExternalCashDeposit")
);
const PaymentRequestPage = lazy(() => import("./Pages/PaymentRequest"));
const BranchesPage = lazy(() => import("./Pages/Branches"));
const BranchDetailsPage = lazy(() =>
  import("./Pages/Branches/BranchDetailsPage")
);
const ButlerServicePage = lazy(() => import("./Pages/ButlerService"));
const QuickPayPage = lazy(() => import("./Pages/QuickPay"));
const TransferAbroadPage = lazy(() => import("./Pages/TransferAbroad"));
const VatPage = lazy(() => import("./Pages/Vat"));
const TaxesPage = lazy(() => import("./Pages/Taxes"));
const HttpRecorderControls = lazy(() =>
  import("./Components/HttpRecorderControls")
);

const identityValue = process.env.REACT_APP_IDENTITY_VALUE;
const secretKey = process.env.REACT_APP_SECRET_KEY;

function aesDecrypt(encryptedText, encryptionKey) {
  try {
    const iv = CryptoJS.enc.Utf8.parse(identityValue);
    const key = CryptoJS.enc.Utf8.parse(encryptionKey);
    const decrypted = CryptoJS.AES.decrypt(encryptedText, key, {
      iv,
      mode: CryptoJS.mode.CBC,
      padding: CryptoJS.pad.Pkcs7,
    });

    return decrypted.toString(CryptoJS.enc.Utf8);
  } catch (error) {
    console.error("Decryption error:", error);
    return null;
  }
}

function parseWebSocketPayload(payload) {
  if (!payload) return null;
  if (typeof payload === "object") return payload;

  try {
    return JSON.parse(payload);
  } catch {
    return null;
  }
}

function parseWebSocketMessage(data) {
  const parsedMessage = JSON.parse(data);

  if (typeof parsedMessage !== "string") {
    return parsedMessage;
  }

  try {
    return JSON.parse(parsedMessage);
  } catch {
    return parsedMessage;
  }
}

function getWebSocketEventText(message, payload) {
  return [
    message?.EventCategory,
    message?.Event,
    message?.Type,
    message?.Action,
    message?.Status,
    payload?.EventCategory,
    payload?.Event,
    payload?.Type,
    payload?.Action,
    payload?.Status,
    payload?.Message,
    payload?.ActionType,
    payload?.AuthorizationStatus,
    payload?.QrStatus,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
}

function isCorporateCardWebSocketMessage(message, payload) {
  return /(card|authorization|authorisation|qr|validation)/i.test(
    getWebSocketEventText(message, payload)
  );
}

const WorkspaceRoutes = memo(function WorkspaceRoutes({
  sessionVersion,
  onOpenQrAfterGenerate,
  onCloseQrReset,
  onRequestCardReveal,
}) {
  return (
    <Routes key={sessionVersion}>
      <Route
        path="overview"
        element={<OverviewPage onOpenQrAfterGenerate={onOpenQrAfterGenerate} />}
      />
      <Route
        path="accountoverview"
        element={<OverviewPage onOpenQrAfterGenerate={onOpenQrAfterGenerate} />}
      />
      <Route path="account" element={<AccountPage />} />
      <Route
        path="account/:accountType"
        element={<Navigate to="/account" replace />}
      />
      <Route
        path="bills"
        element={<BillsPage onOpenQrAfterGenerate={onOpenQrAfterGenerate} />}
      />
      <Route
        path="bills/:provider"
        element={
          <BillServicePage onOpenQrAfterGenerate={onOpenQrAfterGenerate} />
        }
      />
      <Route
        path="exchange"
        element={<ExchangePage onOpenQrAfterGenerate={onOpenQrAfterGenerate} />}
      />
      <Route
        path="suyooltosuyool"
        element={
          <SuyoolToSuyool
            onOpenQrAfterGenerate={onOpenQrAfterGenerate}
            onCloseQrReset={onCloseQrReset}
          />
        }
      />
      <Route
        path="makepayment"
        element={
          <MakePaymentPage onOpenQrAfterGenerate={onOpenQrAfterGenerate} />
        }
      />
      <Route
        path="payroll"
        element={<PayrollPage onOpenQrAfterGenerate={onOpenQrAfterGenerate} />}
      />
      <Route
        path="payroll/payrollsummary"
        element={
          <PayrollPage
            isSummaryRoute
            onOpenQrAfterGenerate={onOpenQrAfterGenerate}
          />
        }
      />
      <Route path="bankaccountoverview" element={<BankAccountOverviewPage />} />
      <Route
        path="transactionhistory"
        element={
          <StatementOfAccountPage
            onOpenQrAfterGenerate={onOpenQrAfterGenerate}
          />
        }
      />
      <Route
        path="bankaccounttransactionhistory"
        element={<BankAccountTransactionHistoryPage />}
      />
      <Route
        path="bankaccounttransfers"
        element={
          <BankAccountTransfersPage
            onOpenQrAfterGenerate={onOpenQrAfterGenerate}
          />
        }
      />
      <Route
        path="banktransfers"
        element={
          <BankAccountTransfersPage
            onOpenQrAfterGenerate={onOpenQrAfterGenerate}
          />
        }
      />
      <Route path="BSLOverview" element={<BankAccountOverviewPage />} />
      <Route path="bsloverview" element={<BankAccountOverviewPage />} />
      <Route
        path="bsltosuyool"
        element={
          <BslToSuyoolPage onOpenQrAfterGenerate={onOpenQrAfterGenerate} />
        }
      />
      <Route
        path="suyooltobsl"
        element={
          <BslToSuyoolPage onOpenQrAfterGenerate={onOpenQrAfterGenerate} />
        }
      />
      <Route
        path="cashin"
        element={
          <CashDeliveryPage
            flowType="cashin"
            onOpenQrAfterGenerate={onOpenQrAfterGenerate}
          />
        }
      />
      <Route
        path="cashout"
        element={
          <CashDeliveryPage
            flowType="cashout"
            onOpenQrAfterGenerate={onOpenQrAfterGenerate}
          />
        }
      />
      <Route
        path="pendingapprovals"
        element={
          <PendingApprovalsPage onOpenQrAfterGenerate={onOpenQrAfterGenerate} />
        }
      />
      <Route
        path="pendingapprovals/pendingsummary"
        element={
          <PendingSummaryPage onOpenQrAfterGenerate={onOpenQrAfterGenerate} />
        }
      />
      <Route
        path="paycorporations"
        element={
          <PayCorporationsPage onOpenQrAfterGenerate={onOpenQrAfterGenerate} />
        }
      />
      <Route
        path="cardssettings"
        element={
          <CardsSettingsPage
            onOpenQrAfterGenerate={onOpenQrAfterGenerate}
            onRequestCardReveal={onRequestCardReveal}
          />
        }
      />
      <Route
        path="cardtransactions"
        element={
          <CardTransactionsPage
            onOpenQrAfterGenerate={onOpenQrAfterGenerate}
            onRequestCardReveal={onRequestCardReveal}
          />
        }
      />
      <Route
        path="cardtransactions/:cardId"
        element={
          <CardTransactionsPage
            onOpenQrAfterGenerate={onOpenQrAfterGenerate}
            onRequestCardReveal={onRequestCardReveal}
          />
        }
      />
      <Route
        path="statementofaccount"
        element={
          <StatementOfAccountPage
            onOpenQrAfterGenerate={onOpenQrAfterGenerate}
          />
        }
      />
      <Route
        path="sales-transactions"
        element={
          <StatementOfAccountPage
            onOpenQrAfterGenerate={onOpenQrAfterGenerate}
          />
        }
      />
      <Route
        path="merchantinformation"
        element={
          <MerchantInformationPage
            onOpenQrAfterGenerate={onOpenQrAfterGenerate}
          />
        }
      />
      <Route
        path="externalcashdeposit"
        element={
          <ExternalCashDepositPage
            onOpenQrAfterGenerate={onOpenQrAfterGenerate}
          />
        }
      />
      <Route
        path="paymentrequest"
        element={
          <PaymentRequestPage onOpenQrAfterGenerate={onOpenQrAfterGenerate} />
        }
      />
      <Route
        path="branches"
        element={<BranchesPage onOpenQrAfterGenerate={onOpenQrAfterGenerate} />}
      />
      <Route path="branches/:branchId" element={<BranchDetailsPage />} />
      <Route
        path="eodclosing"
        element={
          <EodClosingPage onOpenQrAfterGenerate={onOpenQrAfterGenerate} />
        }
      />
      <Route
        path="merchant-enrollment/:step"
        element={<MerchantEnrollmentPage />}
      />
      <Route
        path="butlerservice"
        element={
          <ButlerServicePage onOpenQrAfterGenerate={onOpenQrAfterGenerate} />
        }
      />
      <Route
        path="quickpay"
        element={<QuickPayPage onOpenQrAfterGenerate={onOpenQrAfterGenerate} />}
      />
      <Route
        path="transferabroad"
        element={
          <TransferAbroadPage onOpenQrAfterGenerate={onOpenQrAfterGenerate} />
        }
      />
      <Route
        path="vat"
        element={<VatPage onOpenQrAfterGenerate={onOpenQrAfterGenerate} />}
      />
      <Route
        path="incometax"
        element={<TaxesPage onOpenQrAfterGenerate={onOpenQrAfterGenerate} />}
      />
      <Route
        path="employees"
        element={
          <EmployeesPage onOpenQrAfterGenerate={onOpenQrAfterGenerate} />
        }
      />

      <Route
        path="bulk-transfers"
        element={
          <BulkTransferComposer onOpenQrAfterGenerate={onOpenQrAfterGenerate} />
        }
      />

      <Route
        path="merchants"
        element={<Merchants onOpenQrAfterGenerate={onOpenQrAfterGenerate} />}
      />
      <Route path="*" element={<Navigate to="/overview" replace />} />
    </Routes>
  );
});

function SidebarWorkspace() {
  const dispatch = useDispatch();
  const [, setDecryptedWebSocketUrl] = useState("");
  const webSocketClientRef = useRef(null);
  const encryptedWebSocketUrl = useSelector(
    (state) => state.app?.websocket?.encryptedUrl
  );
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarHovered, setSidebarHovered] = useState(false);
  const [isDesktopSidebar, setIsDesktopSidebar] = useState(() =>
    typeof window === "undefined"
      ? true
      : window.matchMedia("(min-width: 992px)").matches
  );
  const session = useSidebarSession();
  const { logout } = useNewLogoutAPI();
  const logoutRef = useRef(logout);
  logoutRef.current = logout;
  const entityId = sessionStorage.getItem(SESSION_STORAGE_KEYS.ENTITY_ID) || "";
  const {
    processCardDetailSocketEvent,
    processCardRequestSocketEvent,
    processVirtualCardAuthorizationSocketEvent,
    requestCardRevealValidation,
  } = useCardDetailsRevealFlow();
  const cardDetailEventHandlerRef = useRef(processCardDetailSocketEvent);
  const cardRequestEventHandlerRef = useRef(processCardRequestSocketEvent);
  const virtualCardAuthorizationEventHandlerRef = useRef(
    processVirtualCardAuthorizationSocketEvent
  );
  const {
    isOpenQr,
    onCloseQrReset,
    onCloseQrWhilePending,
    onOpenQrAfterGenerate,
  } = useAuthorizationQrFlow();
  const isGlobalLoading = useGlobalLoading();

  const routeProps = useMemo(
    () => ({
      sessionVersion: session.sessionVersion,
      onCloseQrReset,
    }),
    [onCloseQrReset, session.sessionVersion]
  );
  const shouldShowHttpRecorder = canUseHttpRecorder(entityId);

  useEffect(() => {
    cardDetailEventHandlerRef.current = processCardDetailSocketEvent;
    cardRequestEventHandlerRef.current = processCardRequestSocketEvent;
    virtualCardAuthorizationEventHandlerRef.current =
      processVirtualCardAuthorizationSocketEvent;
  }, [
    processCardDetailSocketEvent,
    processCardRequestSocketEvent,
    processVirtualCardAuthorizationSocketEvent,
  ]);

  useEffect(() => {
    const inactivityTimer = new InactivityTimeout(
      inactivityDuration,
      () => logoutRef.current()
    );
    return () => inactivityTimer.destroy();
  }, []);

  useEffect(() => {
    dispatch(fetchTransactionsToApproveCount()).catch(() => {});
  }, [dispatch, session.sessionVersion]);

  useEffect(() => {
    let encryptedUrl = encryptedWebSocketUrl;

    if (!encryptedUrl) {
      encryptedUrl = localStorage.getItem("encryptedWebSocketUrl");
    } else {
      localStorage.setItem("encryptedWebSocketUrl", encryptedUrl);
    }

    if (!encryptedUrl) return undefined;

    const decryptedUrl = aesDecrypt(encryptedUrl, secretKey);
    if (!decryptedUrl) {
      console.error("Failed to decrypt WebSocket URL");
      return undefined;
    }

    setDecryptedWebSocketUrl(decryptedUrl);

    if (
      webSocketClientRef.current &&
      [WebSocket.CONNECTING, WebSocket.OPEN].includes(
        webSocketClientRef.current.readyState
      )
    ) {
      webSocketClientRef.current.close();
    }

    const ws = new WebSocket(decryptedUrl);
    ws.onopen = () => {
      console.log("WebSocket Connected!");
      dispatch(setWebSocketConnected(true));
    };

    ws.onmessage = (event) => {
      try {
        const message = parseWebSocketMessage(event.data);

        if (!message || typeof message !== "object") {
          console.warn("[New WebSocket] ignored non-object message:", message);
          return;
        }

        const payload = parseWebSocketPayload(message.Payload);
        const normalizedMessage = { ...message, Payload: payload };
        const isCorporateCardMessage = isCorporateCardWebSocketMessage(
          message,
          payload
        );

        dispatch(addWebSocketEvent(normalizedMessage));
        if (message.Event === "Refresh GetPayrollSubmitted API") {
          dispatch(
            setFeatureData({
              section: "details",
              key: "payroll",
              value: {
                payrollRefreshRequested: true,
                payrollRefreshRequestedAt: Date.now(),
              },
            })
          );
        }

        virtualCardAuthorizationEventHandlerRef.current?.(normalizedMessage);
        cardDetailEventHandlerRef.current?.(normalizedMessage);
        cardRequestEventHandlerRef.current?.(normalizedMessage);
      } catch (error) {
        console.error("Error parsing WebSocket message:", error);
      }
    };

    ws.onerror = (error) => {
      console.error("WebSocket error:", error);
    };

    ws.onclose = () => {
      console.log("WebSocket connection closed.");
      dispatch(setWebSocketConnected(false));
    };

    webSocketClientRef.current = ws;

    return () => {
      if ([WebSocket.CONNECTING, WebSocket.OPEN].includes(ws.readyState)) {
        ws.close();
        console.log("WebSocket connection closed during cleanup.");
      }
    };
  }, [dispatch, encryptedWebSocketUrl]);

  const sidebarVisible = sidebarOpen || sidebarHovered;
  const closeSidebar = useCallback(() => {
    setSidebarOpen(false);
    setSidebarHovered(false);
  }, []);

  const handleSidebarNavigate = useCallback(() => {
    if (isDesktopSidebar) {
      setSidebarOpen(true);
      setSidebarHovered(false);
      return;
    }

    closeSidebar();
  }, [closeSidebar, isDesktopSidebar]);

  useEffect(() => {
    const mediaQuery = window.matchMedia("(min-width: 992px)");
    const syncSidebarBreakpoint = () => {
      setIsDesktopSidebar(mediaQuery.matches);
    };

    syncSidebarBreakpoint();

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener("change", syncSidebarBreakpoint);
      return () =>
        mediaQuery.removeEventListener("change", syncSidebarBreakpoint);
    }

    mediaQuery.addListener(syncSidebarBreakpoint);
    return () => mediaQuery.removeListener(syncSidebarBreakpoint);
  }, []);

  return (
    <div className="new-app">
      <RenovatedSidebar
        isOpen={sidebarOpen}
        isHovered={sidebarHovered}
        isVisible={sidebarVisible}
        onOpen={() => setSidebarOpen(true)}
        onClose={closeSidebar}
        onHoverChange={(nextHovered) => {
          if (isDesktopSidebar && nextHovered) {
            setSidebarOpen(true);
            setSidebarHovered(false);
            return;
          }

          if (!isDesktopSidebar) {
            setSidebarHovered(nextHovered);
          }
        }}
        onNavigate={handleSidebarNavigate}
        showBackdrop={!isDesktopSidebar}
      />
      <main className="new-app__main">
        <Navbar
          sidebarOpen={sidebarVisible}
          setSidebarOpen={setSidebarOpen}
          session={session}
        />
        <div className="new-app__content">
          <Suspense fallback={null}>
            <WorkspaceRoutes
              {...routeProps}
              onOpenQrAfterGenerate={onOpenQrAfterGenerate}
              onRequestCardReveal={requestCardRevealValidation}
            />
          </Suspense>
        </div>
      </main>
      <Suspense fallback={null}>
        <PortalActionsQr isOpen={isOpenQr} onClose={onCloseQrWhilePending} />
        <ValidationPopup onOpenQrAfterGenerate={onOpenQrAfterGenerate} />
      </Suspense>
      <BlurLoading show={isGlobalLoading} />
      {shouldShowHttpRecorder ? (
        <div className="new-app__http-recorder">
          <Suspense fallback={null}>
            <HttpRecorderControls />
          </Suspense>
        </div>
      ) : null}
    </div>
  );
}
export default function NewApp() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<NewBarcode />} />
        <Route
          path="enrollment/:step"
          element={<Enrollment entity="legal" />}
        />
        <Route
          path="/*"
          element={
            <NewRouteGuard>
              <SidebarWorkspace />
            </NewRouteGuard>
          }
        />
      </Routes>
    </BrowserRouter>
  );
}
